import React, { useState } from 'react';
import { supabase, registrarActividad } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { z } from 'zod';
import imageCompression from 'browser-image-compression';
import '../styles/NewsManager.css';

const noticiaSchema = z.object({
    titulo: z.string().min(10, "Título: mín. 10 caracteres").max(100),
    resumen: z.string().min(20, "Resumen: mín. 20 caracteres").max(350),
    cuerpo: z.string().min(50, "Contenido: mín. 50 caracteres"),
});

const NewsManager = ({ onClose, onFeedback }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({ titulo: '', resumen: '', cuerpo: '' });
    const [imageFile, setImageFile] = useState(null);

    // ✅ Estado para errores locales (visibles dentro del modal)
    const [localError, setLocalError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalError("");

        // 1. Validación Zod
        const validation = noticiaSchema.safeParse(formData);
        if (!validation.success) {
            return setLocalError(`⚠️ ${validation.error.issues[0].message}`);
        }

        if (!imageFile) return setLocalError("❌ Selecciona una imagen");

        try {
            setLoading(true);

            // 2. Compresión de Imagen
            const options = { maxSizeMB: 0.8, maxWidthOrHeight: 1200, useWebWorker: true, fileType: 'image/webp' };
            const compressedFile = await imageCompression(imageFile, options);

            // 3. Subida a Storage
            const fileName = `${Date.now()}_noticia.webp`;
            const storagePath = `noticias/${fileName}`;
            const { error: uploadError } = await supabase.storage.from('archivos').upload(storagePath, compressedFile);
            if (uploadError) throw new Error("Fallo al subir imagen.");

            // 4. Inserción en DB
            const { data: urlData } = supabase.storage.from('archivos').getPublicUrl(storagePath);
            const { error: dbError } = await supabase.from('noticias').insert([{
                titulo: formData.titulo, resumen: formData.resumen, cuerpo: formData.cuerpo,
                imagen_url: urlData.publicUrl, storage_path: storagePath
            }]);

            if (dbError) throw dbError;

            await registrarActividad(user.email, `Publicó noticia: ${formData.titulo}`);

            // ✅ Notificamos éxito y cerramos
            onFeedback("🚀 ¡Noticia publicada con éxito!", "success");
            onClose();
            // ❌ YA NO LLAMAMOS A onUpdate() -> El Hook Realtime lo hará solo

        } catch (err) {
            setLocalError(`❌ ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modern-news-form-container">
            <form className="modern-news-form" onSubmit={handleSubmit}>
                <div className="form-header">
                    <h3 style={{ margin: 0 }}><span>📰</span> Nueva Noticia</h3>
                    <button type="button" className="close-modal-btn" onClick={onClose}>&times;</button>
                </div>

                {/* ✅ Alerta interna para errores de validación */}
                {localError && (
                    <div className="local-error-alert" style={{ background: '#fee2e2', color: '#dc2626', padding: '12px', borderRadius: '10px', marginBottom: '15px', fontSize: '0.9rem', border: '1px solid #fecaca' }}>
                        {localError}
                    </div>
                )}

                <div className="input-group">
                    <label>Título Institucional</label>
                    <input type="text" placeholder="Ej: Nueva obra en plaza..." value={formData.titulo} onChange={e => setFormData({...formData, titulo: e.target.value})} />
                </div>

                <div className="input-group">
                    <label>Resumen Corto</label>
                    <textarea placeholder="Párrafo inicial..." value={formData.resumen} onChange={e => setFormData({...formData, resumen: e.target.value})} />
                </div>

                <div className="input-group">
                    <label>Contenido</label>
                    <textarea className="cuerpo-textarea" style={{ minHeight: '150px' }} value={formData.cuerpo} onChange={e => setFormData({...formData, cuerpo: e.target.value})} />
                </div>

                <div className="file-input-group">
                    <label className="file-label">Foto de Portada</label>
                    <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files[0])} />
                </div>

                <button type="submit" className="btn-publish-modal" disabled={loading}>
                    {loading ? "Procesando..." : "Publicar Noticia Ahora"}
                </button>
            </form>
        </div>
    );
};

export default NewsManager;